import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Phone,
  Search,
  MapPin,
  Home,
  IndianRupee,
  ShieldCheck,
  Star,
} from "lucide-react";
import "./HeroSection.css";

const PURPOSE_TABS = [
  { id: "Buy", label: "Buy" },
  { id: "Rent", label: "Rent" },
  { id: "Commercial", label: "Commercial" },
  { id: "Sell", label: "Sell", isExternal: true },
];

const POPULAR_CITIES = [
  "All Cities",
  "Bangalore",
  "Mumbai",
  "Delhi NCR",
  "Noida",
  "Ghaziabad",
  "Pune",
  "Hyderabad",
];

const PROPERTY_TYPES = [
  "All Types",
  "Apartment / Flat",
  "Independent Villa",
  "Residential House",
  "Commercial Office",
  "Plot / Land",
];

const BUDGET_RANGES = [
  { label: "Any Budget", value: "" },
  { label: "Under ₹50 Lac", value: "0-5000000" },
  { label: "₹50 Lac - ₹1.5 Cr", value: "5000000-15000000" },
  { label: "₹1.5 Cr - ₹3 Cr", value: "15000000-30000000" },
  { label: "₹3 Cr & Above", value: "30000000+" },
];

const HeroSection = () => {
  const navigate = useNavigate();
  const [activePurpose, setActivePurpose] = useState("Buy");
  const [selectedCity, setSelectedCity] = useState("All Cities");
  const [selectedType, setSelectedType] = useState("All Types");
  const [selectedBudget, setSelectedBudget] = useState("");

  const handleSearch = (e) => {
    e.preventDefault();
    if (activePurpose === "Sell") {
      navigate("/partner/signup");
      return;
    }

    const params = new URLSearchParams();
    if (activePurpose) params.append("purpose", activePurpose);
    if (selectedCity && selectedCity !== "All Cities") params.append("city", selectedCity);
    if (selectedType && selectedType !== "All Types") params.append("type", selectedType);
    if (selectedBudget) params.append("budget", selectedBudget);

    navigate(`/property-list?${params.toString()}`);
  };

  return (
    <section className="hero">
      <div className="hero__bg" />
      <div className="hero__gradient" />
      <div className="hero__gradient-bottom" />

      <div className="hero__inner">
        <div className="hero__container">
          {/* Eyebrow badge */}
          <div className="hero__eyebrow">
            <span className="hero__eyebrow-dot" />
            <span className="hero__eyebrow-text">
              ● India's Trusted Real Estate & Home Ecosystem
            </span>
          </div>

          <h1 className="hero__heading">
            Find Your <span className="hero__highlight">Dream Property</span> Today
          </h1>

          <p className="hero__subheading">
            Discover exceptional homes, luxury villas, and smart investment opportunities across India — transparent, verified, and tailored to you.
          </p>

          <div className="hero__actions">
            <Link to="/property-list" className="hero__btn-primary">
              <span>View Listings</span>
              <ArrowRight size={16} strokeWidth={2.5} />
            </Link>

            <Link to="/contactus" className="hero__btn-secondary">
              <Phone size={15} strokeWidth={2} />
              <span>Contact Us</span>
            </Link>

            <span className="hero__divider" />

            <Link to="/partner/signup" className="hero__btn-ghost">
              <ShieldCheck size={15} />
              <span>Post Your Property</span>
            </Link>
          </div>

          {/* Integrated SaaS Property Search Bar */}
          <div className="hero-search">
            <div className="hero-search__tabs">
              {PURPOSE_TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`hero-search__tab ${
                    activePurpose === tab.id ? "hero-search__tab--active" : ""
                  }`}
                  onClick={() => {
                    if (tab.isExternal) {
                      navigate("/partner/signup");
                    } else {
                      setActivePurpose(tab.id);
                    }
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <form onSubmit={handleSearch} className="hero-search__form">
              <div className="hero-search__field">
                <div className="hero-search__field-label">
                  <MapPin size={14} className="hero-search__icon" />
                  <span>Location</span>
                </div>
                <select
                  className="hero-search__select"
                  value={selectedCity}
                  onChange={(e) => setSelectedCity(e.target.value)}
                >
                  {POPULAR_CITIES.map((city) => (
                    <option key={city} value={city}>
                      {city}
                    </option>
                  ))}
                </select>
              </div>

              <div className="hero-search__separator" />

              <div className="hero-search__field">
                <div className="hero-search__field-label">
                  <Home size={14} className="hero-search__icon" />
                  <span>Property Type</span>
                </div>
                <select
                  className="hero-search__select"
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                >
                  {PROPERTY_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              <div className="hero-search__separator" />

              <div className="hero-search__field">
                <div className="hero-search__field-label">
                  <IndianRupee size={14} className="hero-search__icon" />
                  <span>Budget Range</span>
                </div>
                <select
                  className="hero-search__select"
                  value={selectedBudget}
                  onChange={(e) => setSelectedBudget(e.target.value)}
                >
                  {BUDGET_RANGES.map((b) => (
                    <option key={b.label} value={b.value}>
                      {b.label}
                    </option>
                  ))}
                </select>
              </div>

              <button type="submit" className="hero-search__submit-btn">
                <Search size={16} strokeWidth={2.5} />
                <span>Search Properties</span>
              </button>
            </form>
          </div>

          {/* Trust Statistics Strip */}
          <div className="hero-stats">
            <div className="hero-stat-item">
              <div className="hero-stat-item__number">10,000+</div>
              <div className="hero-stat-item__label">Happy Homeowners</div>
            </div>
            <div className="hero-stat-divider" />
            <div className="hero-stat-item">
              <div className="hero-stat-item__number">25,000+</div>
              <div className="hero-stat-item__label">Verified Listings</div>
            </div>
            <div className="hero-stat-divider" />
            <div className="hero-stat-item">
              <div className="hero-stat-item__number">15+</div>
              <div className="hero-stat-item__label">Major Cities Covered</div>
            </div>
            <div className="hero-stat-divider" />
            <div className="hero-stat-item">
              <div className="hero-stat-item__number hero-stat-item__rating">
                <span>4.9</span>
                <Star size={14} fill="#f59e0b" color="#f59e0b" />
              </div>
              <div className="hero-stat-item__label">Customer Trust Score</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;